import React from 'react';
import { motion } from 'framer-motion';
import { FaTimes, FaPrint, FaDownload } from 'react-icons/fa';
import { ProjectLog } from './AdminDashboard';

interface ResumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: ProjectLog[];
}

export const ResumeModal: React.FC<ResumeModalProps> = ({ isOpen, onClose, projects }) => {
  if (!isOpen) return null;

  const publishedProjects = projects.filter(p => p.published);

  const handlePrint = () => {
    // Open print dialog for just the resume container
    const printContent = document.getElementById('printable-resume-area')?.innerHTML;
    const originalContent = document.body.innerHTML;
    
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Alok Singh - Resume</title>
              <script src="https://cdn.tailwindcss.com"></script>
              <style>
                body { font-family: sans-serif; padding: 2rem; color: #1f2937; }
                a { text-decoration: none; color: inherit; }
                @media print {
                  body { padding: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="max-w-4xl mx-auto">
                ${printContent}
              </div>
              <script>
                window.onload = function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="max-w-4xl w-full h-[90vh] bg-cyber-darker border border-cyber-cyan/30 rounded-2xl shadow-glow-cyan flex flex-col overflow-hidden"
      >
        {/* Modal Controls Bar */}
        <div className="flex justify-between items-center bg-cyber-dark px-6 py-4 border-b border-cyber-cyan/15 flex-shrink-0">
          <div className="flex items-center space-x-2 font-mono text-xs text-cyber-cyan font-bold">
            <span className="w-2 h-2 rounded-full bg-cyber-cyan animate-pulse" />
            <span>ALOK_SINGH // RESUME_PREVIEW</span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrint}
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-600/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded font-mono text-xs tracking-wider transition-all duration-300"
              title="Print Resume"
            >
              <FaPrint size={12} />
              <span className="hidden sm:inline">PRINT_PDF</span>
            </button>
            <button
              onClick={onClose}
              className="text-cyber-gray-400 hover:text-red-500 font-mono text-xs transition duration-200 flex items-center space-x-1"
            >
              <FaTimes size={14} />
              <span>[CLOSE]</span>
            </button>
          </div>
        </div>

        {/* Printable/Scrollable Resume Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-white text-gray-800 font-sans">
          <div id="printable-resume-area" className="max-w-3xl mx-auto space-y-6">
            
            {/* Header Section */}
            <div className="border-b-2 border-gray-900 pb-4 text-center sm:text-left flex flex-col sm:flex-row sm:justify-between sm:items-end">
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-gray-900">Alok Singh</h1>
                <p className="text-xl font-medium text-emerald-600 mt-1">Full-Stack Web Developer</p>
              </div>
              <div className="mt-4 sm:mt-0 text-sm text-gray-600 space-y-1 text-center sm:text-right">
                <p>📍 Ghaziabad, UP | 📞 +91 8318739431</p>
                <p>✉️ <a href="mailto:kshatriyaalok1997@gmail.com" className="hover:underline">kshatriyaalok1997@gmail.com</a></p>
                <p>🔗 <a href="https://github.com/aloki9singh" target="_blank" className="hover:underline font-medium">github.com/aloki9singh</a> | <a href="https://aloki9singh.github.io/" target="_blank" className="hover:underline font-medium">Portfolio</a></p>
              </div>
            </div>

            {/* Summary */}
            <div className="mt-6">
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-900 border-b border-gray-300 pb-1">Professional Summary</h2>
              <p className="mt-2 text-gray-700 leading-relaxed text-sm">
                Result-oriented Full-Stack Web Developer with enterprise experience at GrowingPro Technologies and a solid foundation of 2000+ hours of practical coding and 200+ Data Structures & Algorithms (DSA) problems solved. Specialized in architecting scalable internal tools, managing relational and non-relational databases (PostgreSQL, MongoDB), and optimizing backend execution using TypeScript, PM2, and Node.js.
              </p>
            </div>

            {/* Technical Skills */}
            <div className="mt-6">
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-900 border-b border-gray-300 pb-1">Technical Skills</h2>
              <div className="mt-2 space-y-2 text-sm">
                <p><strong className="text-gray-900">Languages & Frameworks:</strong> TypeScript, JavaScript (ES6+), React.js, Node.js, Express.js, HTML5, CSS3</p>
                <p><strong className="text-gray-900">Databases & Architecture:</strong> PostgreSQL, MongoDB, pgAdmin 4, MERN Stack</p>
                <p><strong className="text-gray-900">DevOps & Deployment:</strong> PM2, Terminus, Git/GitHub, Linux Environment</p>
                <p><strong className="text-gray-900">UI Frameworks & State Management:</strong> Tailwind CSS, Responsive Web Design, Redux</p>
              </div>
            </div>

            {/* Experience */}
            <div className="mt-6">
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-900 border-b border-gray-300 pb-1">Professional Experience</h2>
              
              <div className="mt-3">
                <div className="flex justify-between items-baseline">
                  <h3 className="font-bold text-gray-900">GrowingPro Technologies <span className="font-normal text-gray-600">| Noida, Sector 63</span></h3>
                  <span className="text-xs font-semibold text-gray-500">Oct 2025 – Present</span>
                </div>
                <p className="text-sm font-medium text-emerald-600">Full-Stack Developer</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-gray-700 space-y-1">
                  <li>Architected and deployed a scalable internal Enterprise CRM System using TypeScript, Node.js, and PostgreSQL to optimize business workflows.</li>
                  <li>Designed highly responsive administrative dashboards using Tailwind CSS, streamlining operations for dynamic data rendering.</li>
                  <li>Managed database health, structure optimization, and script performance across MongoDB and PostgreSQL setups via pgAdmin 4.</li>
                  <li>Maintained 99.9% application uptime and live backend processes using PM2 configuration tools.</li>
                </ul>
              </div>
            </div>

            {/* Technical Projects */}
            <div className="mt-6">
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-900 border-b border-gray-300 pb-1">Technical Projects</h2>
              
              <div className="space-y-4">
                {publishedProjects.length > 0 ? (
                  publishedProjects.map((proj) => {
                    const links: React.ReactNode[] = [];
                    if (proj.githubUrl) {
                      links.push(<a href={proj.githubUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">GitHub</a>);
                    }
                    if (proj.liveUrl) {
                      links.push(<a href={proj.liveUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-bold">Live Link</a>);
                    }
                    
                    return (
                      <div key={proj.id} className="mt-3">
                        <div className="flex justify-between items-baseline">
                          <h3 className="font-bold text-gray-900">
                            {proj.name}
                            {links.length > 0 && (
                              <span className="text-xs font-normal text-gray-500 ml-2">
                                ({links.map((link, idx) => (
                                  <React.Fragment key={idx}>
                                    {link}
                                    {idx < links.length - 1 && ' | '}
                                  </React.Fragment>
                                ))})
                              </span>
                            )}
                          </h3>
                        </div>
                        <p className="text-xs text-gray-600 font-medium">Tech Stack: {proj.badges.join(' | ')}</p>
                        <ul className="list-disc pl-5 mt-1 text-sm text-gray-700 space-y-0.5">
                          {proj.description.split('\n').map((line, idx) => (
                            <li key={idx}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div className="mt-3">
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-bold text-gray-900">OwnWay <span className="text-xs font-normal text-gray-500">(MERN CRUD Web Application)</span></h3>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">Tech Stack: React | MongoDB | Express | Node.js | JWT | Bcrypt</p>
                      <ul className="list-disc pl-5 mt-1 text-sm text-gray-700 space-y-0.5">
                        <li>Developed a custom-styled, fully responsive web application implementing end-to-end CRUD functionality.</li>
                        <li>Built a secure admin dashboard leveraging JWT authentication and Bcrypt hashing to safely manage dynamic user posts.</li>
                      </ul>
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-bold text-gray-900">MyntShop <span className="text-xs font-normal text-gray-500">(E-Commerce Frontend Clone)</span></h3>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">Tech Stack: React | Redux | JavaScript | Chakra-UI | Tailwind</p>
                      <ul className="list-disc pl-5 mt-1 text-sm text-gray-700 space-y-0.5">
                        <li>Engineered an interactive frontend application clone using Redux for predictable, centralized application state management.</li>
                        <li>Implemented modular components for complex features including dynamic filters, client-side pagination, and user auth routing.</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="mt-6">
              <h2 className="text-lg font-bold uppercase tracking-wider text-gray-900 border-b border-gray-300 pb-1">Education</h2>
              <div className="mt-3 space-y-3 text-sm">
                <div className="flex justify-between">
                  <div>
                    <strong className="text-gray-900">Master of Computer Applications (MCA)</strong>
                    <p className="text-gray-600 text-xs">Currently Pursuing (Submitted 2nd-Semester Projects)</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-500">2025 – Present</span>
                </div>
                <div className="flex justify-between">
                  <div>
                    <strong className="text-gray-900">Full-Stack Web Development Certification</strong>
                    <p className="text-gray-600 text-xs">Masai School, Bengaluru (Full-Time Curriculum)</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-500">Graduated 2022</span>
                </div>
                <div className="flex justify-between">
                  <div>
                    <strong className="text-gray-900">Bachelor of Science (B.Sc.)</strong>
                    <p className="text-gray-600 text-xs">Bundelkhand University, Jhansi</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-500">2014 – 2017</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </motion.div>
    </div>
  );
};
